import React, { memo, useState, useRef } from 'react';
import { 
  Zap, 
  Code, 
  Sparkles, 
  Rocket, 
  Star, 
  Heart, 
  Coffee,
  Award,
  Target,
  TrendingUp,
  Users,
  Clock
} from 'lucide-react';
import { useIntersectionObserver } from '../../hooks/useOptimizedHooks';
import GlowingText from '../ui/GlowingText';
import GlassCard from '../ui/GlassCard';
import GradientButton from '../ui/GradientButton';

// Optimized AboutSection with designer styling
const AboutSection = memo(() => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef();
  const { entries, observe } = useIntersectionObserver({ threshold: 0.1 });
  
  React.useEffect(() => {
    if (sectionRef.current) {
      observe(sectionRef.current, 'about-section');
    }
  }, [observe]);
  
  React.useEffect(() => {
    const entry = entries['about-section'];
    if (entry?.isIntersecting) {
      setIsVisible(true);
    }
  }, [entries]);

  const stats = [
    { icon: Award, label: "Projects Completed", value: "15+", color: "from-violet-500 to-purple-500" },
    { icon: Clock, label: "Years Experience", value: "1+", color: "from-blue-500 to-cyan-500" },
    { icon: Users, label: "Happy Clients", value: "10+", color: "from-emerald-500 to-green-500" },
    { icon: TrendingUp, label: "Success Rate", value: "98%", color: "from-orange-500 to-red-500" }
  ];

  const values = [
    {
      icon: Zap,
      title: "Fast Performance",
      description: "Optimized for speed and responsiveness across all devices",
      color: "violet"
    },
    {
      icon: Code,
      title: "Clean Code",
      description: "Writing maintainable and future-proof code with best practices",
      color: "blue"
    },
    {
      icon: Sparkles,
      title: "Creative Design",
      description: "Crafting unique, memorable user experiences that engage",
      color: "cyan"
    },
    {
      icon: Rocket,
      title: "SEO Optimized",
      description: "Building for discoverability, accessibility, and reach",
      color: "pink"
    }
  ];

  const highlights = [
    {
      icon: Star,
      title: "Experience",
      description: "1+ years of professional experience working with modern web technologies and frameworks",
      color: "violet"
    },
    {
      icon: Heart,
      title: "Passion",
      description: "Dedicated to creating high-quality, accessible digital experiences that make a difference",
      color: "blue"
    },
    {
      icon: Coffee,
      title: "Dedication",
      description: "Committed to continuous learning and improving my craft through constant exploration",
      color: "cyan"
    }
  ];

  return (
    <section ref={sectionRef} id="about" className="py-32 px-6 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-violet-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-gradient-to-tr from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="noise-overlay absolute inset-0 opacity-20 pointer-events-none"></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-20 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <span className="text-caption text-violet-400 mb-4 block">Get To Know Me</span>          <GlowingText 
            as="h2"
            size="4xl"
            color="mixed"
            animation="breathing"
            className="mb-6"
          >
            About Me
          </GlowingText>
          <div className="w-20 h-1 bg-gradient-to-r from-violet-500 to-blue-500 mx-auto rounded-full"></div>
        </div>
        
        {/* Main Story Section */}
        <GlassCard 
          color="mixed" 
          variant="raised" 
          glow={true} 
          decorativeElements={true}
          padding="xl"
          rounded="2xl"
          hoverEffect="scale"
          className={`mb-16 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h3 className="text-heading-lg mb-6">              <GlowingText 
                size="2xl"
                color="violet"
                weight="bold"
              >
                My Journey
              </GlowingText>
              </h3>
              
              <p className="text-body-lg text-neutral-300 leading-relaxed">
                With over <span className="text-violet-400 font-semibold">1+ years of experience</span> in web development, 
                I've had the privilege of working with a diverse range of clients and projects. My journey began 
                with a passion for creating user-friendly, accessible web experiences that not only look beautiful 
                but perform exceptionally well.
              </p>
              
              <p className="text-body-lg text-neutral-300 leading-relaxed">
                I specialize in building modern web applications using <span className="text-blue-400 font-semibold">AI, React, Python,</span> 
                and other cutting-edge technologies. My approach combines technical expertise with a keen eye for design, 
                ensuring that every project I work on is both functional and visually stunning.
              </p>

              <p className="text-body-lg text-neutral-300 leading-relaxed">
                When I'm not coding, you'll find me exploring new technologies, contributing to open-source projects, 
                or sharing knowledge with the developer community. I believe in continuous learning and staying 
                ahead of industry trends.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <GradientButton
                  size="md"
                  color="primary"
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Let's Connect
                </GradientButton>
                
                <GradientButton
                  variant="outline"
                  size="md"
                  color="accent"
                  href="/resume.pdf"
                  target="_blank"
                >
                  Download Resume
                </GradientButton>
              </div>
            </div>
            
            {/* Values Grid */}
            <div className="grid grid-cols-2 gap-4">
              {values.map((value, index) => (
                <div
                  key={value.title}
                  className={`bg-slate-800/50 backdrop-blur-sm rounded-xl p-5 border border-slate-700/50 transform hover:-translate-y-2 transition-all duration-300 hover:border-${value.color}-400/50`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <value.icon className={`w-8 h-8 text-${value.color}-400 mb-3`} />
                  <h4 className="text-body-lg font-semibold mb-2 text-neutral-100">{value.title}</h4>
                  <p className="text-caption text-neutral-400">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </GlassCard>

        {/* Stats Section */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 transition-all duration-1000 delay-400 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {stats.map((stat, index) => (
            <GlassCard
              key={stat.label}
              color="slate"
              variant="bordered"
              padding="lg"
              rounded="xl"
              hoverEffect="lift"
              className="text-center group"
            >
              <div className={`inline-flex justify-center items-center w-12 h-12 rounded-full bg-gradient-to-r ${stat.color} bg-opacity-20 mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div className="text-heading-lg font-bold text-neutral-100 mb-1">{stat.value}</div>
              <p className="text-caption text-neutral-400">{stat.label}</p>
            </GlassCard>
          ))}
        </div>

        {/* Highlights Section */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 delay-600 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {highlights.map((highlight, index) => (
            <GlassCard 
              key={highlight.title}
              color={highlight.color} 
              variant="bordered" 
              padding="xl"
              rounded="xl"
              hoverEffect="lift"
              className="text-center group"
            >
              <div className={`inline-flex justify-center items-center w-16 h-16 rounded-full bg-${highlight.color}-500/20 mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <highlight.icon className={`w-8 h-8 text-${highlight.color}-400`} />
              </div>
              <h3 className="text-heading-md font-bold mb-4 text-neutral-100">{highlight.title}</h3>
              <p className="text-body-md text-neutral-300 leading-relaxed">{highlight.description}</p>
            </GlassCard>
          ))}
        </div>

        {/* Call to Action */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-800 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-violet-500/10 to-blue-500/10 border border-violet-500/20">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
            <span className="text-sm text-neutral-300">
              Currently available for freelance work and full-time opportunities
            </span>
          </div>
        </div>
      </div>
    </section>
  );
});

export default AboutSection;
